import { Accept, FileRejection } from 'react-dropzone';
import {
  FieldValues,
  UseControllerProps,
  useController
} from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { DocumentInput } from './document-input';

type DocumentInputProps<T extends FieldValues> = {
  required?: boolean;
  accept?: Accept;
  uploadBlob: (formData: FormData) => Promise<{ id: string }>;
  getBlobInfo: (
    id: string
  ) => Promise<{ id: string; name: string } | undefined>;
  getBlob: (id: string) => Promise<Blob>;
  error?: string;
  allowedFormatText?: string;
  description?: string;
  ctaText?: string;
  maxSize?: number;
  onDropRejected?: (fileRejections: FileRejection[]) => void;
} & Omit<UseControllerProps<T>, 'defaultValue'>;

export function DocumentInputField<T extends FieldValues>(
  props: DocumentInputProps<T>
): JSX.Element {
  const {
    control,
    name,
    required,
    shouldUnregister,
    uploadBlob,
    getBlobInfo,
    getBlob,
    ...rest
  } = props;

  const { field } = useController<T>({
    control,
    name,
    rules: { required },
    shouldUnregister
  });

  const { mutateAsync, isLoading } = useMutation(uploadBlob, {
    onSuccess: ({ id }) => {
      field.onChange(id);
    },
    onError: () => {
      field.onChange('');
    }
  });

  const onDrop = async (acceptedFiles: File[]): Promise<void> => {
    if (!acceptedFiles?.[0]) {
      field.onChange('');
      return;
    }

    const formData = new FormData();

    formData.append('file', acceptedFiles[0]);

    await mutateAsync(formData);
  };

  const { data: blobInfo } = useQuery({
    queryKey: ['blob', field.value],
    queryFn: () => getBlobInfo(field.value),
    enabled: !!field.value
  });

  const { mutate: onDocumentClick } = useMutation(
    (blobInfo: { id: string; name: string }) => getBlob(blobInfo.id),
    {
      onSuccess: (blob, variables) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');

        a.href = url;
        a.download = variables.name;
        a.setAttribute('style', 'display: none;');

        document.body.appendChild(a);

        a.click();
        a.remove();
      }
    }
  );

  return (
    <DocumentInput
      {...rest}
      name={name}
      onDrop={onDrop}
      filename={blobInfo?.name}
      isLoading={isLoading}
      onReset={(): void => {
        field.onChange('');
      }}
      onFileClick={(): void => {
        if (!blobInfo) {
          return;
        }
        onDocumentClick(blobInfo);
      }}
    />
  );
}
